import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../shared/services/api.service';
import { LoaderService } from '../../shared/services/loader.service';
import { FormsModule } from '@angular/forms';

interface FAQQuestion {
  id: number;
  category: string;
  question: string;
  answer: string;
  is_approved: boolean;
  is_active: boolean;
  created_dt: string;
  updated_dt: string;
  isExpanded: boolean;
  category_name: string;
}

interface FAQCategory {
  description: string;
  questions: FAQQuestion[];
}

interface FAQData {
  [key: string]: FAQCategory;
}

interface ApiResponse {
  success: boolean;
  total_count: number;
  data: FAQQuestion[];
}

@Component({
  selector: 'app-profile-faq',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './profile-faq.component.html',
  styleUrl: './profile-faq.component.scss',
})
export class ProfileFaqComponent {
  activeTab: string = '';
  faqData: FAQData = {};
  isLoading: boolean = false;
  originalFaqs: string = ''; // full FAQ list
  searchTerm: string = '';
  faqs: string = ''; // filtered FAQ list
  //selectedCategory: string = '';
  noDataFound: boolean = false;

  expandedQuestions: Set<string> = new Set<string>();

  // Category descriptions mapping
  categoryDescriptions: { [key: string]: string } = {
    'Order & Delivery': 'Information about ordering, tracking & delivery',
    'Return & Refunds':
      'Details on return eligibility, refund timelines & return shipping',
    'Payment & Pricing':
      'Accepted payment methods, taxes, and bulk order discounts',
    'Warranty & Support':
      'Warranty coverage, claims process & technical assistance',
    'Account Related': 'Account management, profile settings & preferences',
  };

  constructor(
    private apiService: ApiService,
    private loaderService: LoaderService,
  ) {}

  ngOnInit(): void {
    this.loadFaqData();
  }

  // Navigation methods
  // setActiveTab(tab: string): void {
  //   this.activeTab = tab;
  //   this.expandedQuestions.clear(); // Collapse all questions when switching tabs
  // }

  setActiveTab(tab: string) {
    this.activeTab = tab;
    this.expandedQuestions.clear(); // collapse everything when tab changes
  }

  // FAQ methods
  // toggleQuestion(category: string, questionIndex: number): void {
  //   this.faqData[category].questions[questionIndex].isExpanded =
  //     !this.faqData[category].questions[questionIndex].isExpanded;
  // }

  //  toggleQuestion(tab: string, index: number) {
  //   if (this.expandedQuestions.has(index)) {
  //     this.expandedQuestions.delete(index);
  //   } else {
  //     this.expandedQuestions.clear();   // ✅ ensures only one question open at a time
  //     this.expandedQuestions.add(index);
  //   }
  // }

  toggleQuestion(parentTab: string, categoryTab: string, index: number) {
    const key = `${parentTab}-${categoryTab}-${index}`;
    if (this.expandedQuestions.has(key)) {
      this.expandedQuestions.delete(key);
    } else {
      this.expandedQuestions.clear(); // accordion style
      this.expandedQuestions.add(key);
    }
  }

  onSearchKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.searchFAQ(); // 👈 call the search
    }
  }

  // Load FAQ data from API
  loadFaqData(): void {
    this.loaderService.show();
    //this.isLoading = true;
    const payload = {
      offset: 0,
      limit: 100, // Increased limit to get all FAQs
    };

    this.apiService.getFaqData(payload).subscribe({
      next: (res: ApiResponse) => {
        console.log('FAQ Data:', res);

        if (res && res.success && res.data) {
          this.categorizeFaqData(res.data);
          this.noDataFound = res.data.length === 0;

          // Set default active tab if not set
          const tabs = Object.keys(this.faqData);
          if (tabs.length > 0) {
            this.activeTab = tabs[0];
          }
        } else {
          console.warn('FAQ response did not contain expected data:', res);
          this.faqData = {};
          this.noDataFound = true;
        }
        this.loaderService.hide();
      },
      error: (err) => {
        console.error('Error fetching FAQs:', err);
        this.noDataFound = true;
        this.loaderService.hide();
        // Optionally show fallback message
        alert(
          'Something went wrong while loading FAQs. Please try again later.',
        );
      },
    });
  }

  getVisibleTabs(): string[] {
    return this.getNavigationTabs().slice(
      this.getNavigationTabs().indexOf(this.activeTab),
    );
    // .filter((tab) => tab !== 'Account Related'); // remove Account Related
  }

  // Categorize FAQ data by category
  private categorizeFaqData(apiData: FAQQuestion[]): void {
    this.faqData = {};
    this.noDataFound = false;

    // Group questions by category
    const categorizedData: { [key: string]: FAQQuestion[] } = {};

    apiData.forEach((question) => {
      if (question.is_active && question.is_approved) {
        const category = question.category_name;
        if (!categorizedData[category]) {
          categorizedData[category] = [];
        }

        if (Object.keys(categorizedData).length === 0) {
          this.noDataFound = true;
        }
        // Add isExpanded property for UI state
        const questionWithExpansion = {
          ...question,
          isExpanded: false,
        };

        categorizedData[category].push(questionWithExpansion);
      }
    });

    // Convert to FAQData structure
    Object.keys(categorizedData).forEach((category) => {
      console.log(category, 'category');
      this.faqData[category] = {
        description:
          this.categoryDescriptions[category] || `Questions about ${category}`,
        questions: categorizedData[category],
      };
    });
  }

  searchFAQ() {
    if (this.searchTerm.trim().length > 3) {
      const payload = {
        search_text: this.searchTerm.trim(),
      };

      console.log(payload.search_text.trim().length, 'hdshjds');

      this.loaderService.show();

      this.apiService.getFaqData(payload).subscribe({
        next: (res: ApiResponse) => {
          console.log('Search FAQ Data:', res);
          if (res && res.success && res.data) {
            this.categorizeFaqData(res.data);
            this.noDataFound = res.data.length === 0;
          } else {
            console.warn('FAQ response did not contain expected data:', res);
            this.faqData = {}; // Clear FAQ data on unexpected response
            this.noDataFound = true;
          }
          this.loaderService.hide();
        },
        error: (err) => {
          console.error('Error searching FAQs:', err);
          this.noDataFound = true;
          this.loaderService.hide();
          alert(
            'Something went wrong while searching FAQs. Please try again later.',
          );
        },
      });
    } else {
      this.loadFaqData();
    }
    // Delay of 300ms before making the API call
  }

  onSearchInput(event: any): void {
    this.searchTerm = event.target.value;
    if (this.searchTerm.trim() === '') {
      this.loadFaqData();
    }
  }

  // isQuestionExpanded(category: string, questionIndex: number): boolean {
  //   return this.faqData[category].questions[questionIndex].isExpanded;
  // }

  // Check if question is expanded
  // isQuestionExpanded(tab: string, index: number): boolean {
  //   return this.activeTab === tab && this.expandedQuestions.has(index);
  // }

  isQuestionExpanded(
    parentTab: string,
    categoryTab: string,
    index: number,
  ): boolean {
    const key = `${parentTab}-${categoryTab}-${index}`;
    return this.expandedQuestions.has(key);
  }

  getActiveTabData(): FAQCategory | undefined {
    return this.faqData[this.activeTab];
  }

  // Helper method to get navigation tabs
  getNavigationTabs(): string[] {
    return Object.keys(this.faqData);
  }

  // Helper method to calculate tab position for mobile layout
  getTabPosition(index: number): string {
    const tabWidth = 140; // Approximate width of each tab
    const leftMargin = 12; // Left margin from container
    return `${leftMargin + index * tabWidth}px`;
  }
}
